/**
 * Keeps the screen on during a call (Screen Wake Lock API — iOS 16.4+,
 * Android Chrome, desktop Chromium). A web app can't keep running once the
 * phone locks, so the most common way a call dropped — the screen timing out
 * mid-conversation — is simply prevented. The browser releases the lock
 * whenever the app leaves the screen; `keepAwake` re-takes it on return.
 */

interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<void>;
}

let sentinel: WakeLockSentinelLike | null = null;
let wanted = false;

async function acquire(): Promise<void> {
  if (!wanted || (sentinel && !sentinel.released) || document.visibilityState !== "visible") return;
  const wakeLock = (navigator as Navigator & {
    wakeLock?: { request(type: "screen"): Promise<WakeLockSentinelLike> };
  }).wakeLock;
  if (!wakeLock) return;
  try {
    sentinel = await wakeLock.request("screen");
    // Released while we were waiting for it.
    if (!wanted) void sentinel.release();
  } catch {
    /* denied (e.g. Low Power Mode) — the call still works, the screen may just dim */
  }
}

function onVisibility(): void {
  if (document.visibilityState === "visible") void acquire();
}

/** Turn "keep the screen on" on or off. */
export function keepAwake(on: boolean): void {
  if (on === wanted) return;
  wanted = on;
  if (on) {
    document.addEventListener("visibilitychange", onVisibility);
    void acquire();
  } else {
    document.removeEventListener("visibilitychange", onVisibility);
    const s = sentinel;
    sentinel = null;
    if (s && !s.released) void s.release().catch(() => undefined);
  }
}
